import { takeFirstWordOfString } from './avatarUtils';

type DisplayableUser = {
  username: string;
  email?: string | null;
  usernameAutoGenerated?: boolean | null;
};

/** Backend `userDisplay` entry: only users with a generated username. */
export type UserDisplayInfo = { name?: string | null; email?: string | null };

/** Backend `userDisplay` map, keyed by username. */
export type UserDisplayMap = Record<string, UserDisplayInfo> | null | undefined;

const GENERATED_USERNAME = /^u_[0-9a-f]{16}$/i;

/** Shape of a generated username (`u_` + 16 hex). Never show one. */
export const looksGenerated = (username?: string | null): boolean =>
  GENERATED_USERNAME.test((username ?? '').trim());

const fullName = (
  firstName?: string | null,
  lastName?: string | null
): string =>
  [firstName, lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

/**
 * New users never chose a username (it is random), so show their email.
 * Existing users keep seeing their username.
 */
export const displayUsername = (user: DisplayableUser): string =>
  user.usernameAutoGenerated && user.email ? user.email : user.username;

/**
 * The display rule for a user object. A generated username is never shown:
 * use the full name, else the email. Existing users keep their username.
 */
export const userMessageName = (
  user: DisplayableUser & {
    firstName?: string | null;
    lastName?: string | null;
  }
): string => {
  if (!user.usernameAutoGenerated) return user.username;
  return fullName(user.firstName, user.lastName) || user.email || '';
};

/**
 * The same rule for a bare username (creator, assignee, owner, audit actor),
 * using the backend `userDisplay` map. Users not in the map keep their
 * username, unless it is a generated one: then this returns ''.
 */
export const usernameDisplayName = (
  username: string | null | undefined,
  userDisplay?: UserDisplayMap
): string => {
  if (!username) return '';
  const entry = userDisplay?.[username];
  const shown = entry?.name?.trim() || entry?.email?.trim();
  if (shown) return shown;
  return looksGenerated(username) ? '' : username;
};

/** Workspace members: `userDisplay` first, then WorkspaceRo.memberEmails. */
export const memberDisplayName = (
  username: string,
  memberEmails?: Record<string, string> | null,
  userDisplay?: UserDisplayMap
): string => {
  const shown = usernameDisplayName(username, userDisplay);
  if (shown && shown !== username) return shown;
  return memberEmails?.[username] || shown;
};

/** Initials from a name, or from the local part of an email. */
export const initialsFrom = (nameOrEmail: string): string => {
  const source = nameOrEmail.includes('@')
    ? nameOrEmail.split('@')[0]
    : nameOrEmail;
  const words = source.split(/[\s._-]+/).filter(Boolean);
  if (!words.length) return '?';
  return (words.length > 1 ? words[0][0] + words[1][0] : words[0][0])
    .toUpperCase();
};

/**
 * Avatar initials for something already run through the display rule.
 * When it is the username itself (existing user), keep the old initials.
 */
export const displayInitials = (
  displayName: string | null | undefined,
  username?: string | null
): string => {
  const shown = (displayName ?? '').trim();
  if (!shown || looksGenerated(shown)) return '?';
  if (shown === username) return takeFirstWordOfString(username);
  return initialsFrom(shown);
};

/**
 * Avatar initials for a bare username. Generated users: from the name, else
 * the email. Existing users keep the initials of their username.
 */
export const usernameInitials = (
  username: string | null | undefined,
  userDisplay?: UserDisplayMap,
  memberEmails?: Record<string, string> | null
): string =>
  displayInitials(
    username ? memberDisplayName(username, memberEmails, userDisplay) : '',
    username
  );
